import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { adminClient, type AttentionResponse } from '@/api/adminClient';
import { ATTENTION_REFRESH_MS, AttentionContext } from '@/hooks/useAttention';

/** Loads "Needs attention" on mount and every minute while the tab is visible; does nothing when `enabled` is false. */
export function AttentionProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const [attention, setAttention] = useState<AttentionResponse | undefined>();
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | undefined>();
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (!enabled || inFlight.current) return;
    inFlight.current = true;
    try {
      setAttention(await adminClient.getAttention());
      setError(undefined);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Could not check what needs attention');
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    void load();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void load();
    }, ATTENTION_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [enabled, load]);

  const value = useMemo(
    () => ({ attention: enabled ? attention : undefined, loading: enabled && loading, error, refresh: () => void load() }),
    [attention, enabled, error, load, loading],
  );

  return <AttentionContext.Provider value={value}>{children}</AttentionContext.Provider>;
}
